import React, { useState, useMemo, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { usePomodoro } from '../context/PomodoroContext';
import { getColors, taskCategories, weightOptions, getModalStyle } from '../theme';
import { X, Edit2, Trash2, Filter, ChevronDown, Archive, RotateCcw, Lock, Timer } from 'lucide-react';
import { UpgradePrompt, UpgradeLimitModal } from '../components/LockedFeature';
import { TasksPageSkeleton } from '../components/LoadingSkeleton';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import PomodoroTimer from '../components/PomodoroTimer';
import SoftUpgradeNudge from '../components/SoftUpgradeNudge';
import { PageHead, QuickAddRow } from '../components/Notebook';
import { useQuickAddInterpreter } from '../agent/quickAdd';

const TasksPage = () => {
  const { isDark, tasks, archivedTasks, addTask, updateTask, deleteTask, archiveTask, restoreTask, permanentlyDeleteTask, dataLoading } = useApp();
  const { hasFeature, canAddMore, getItemLimit, loading: subscriptionLoading } = useSubscription();
  const { activeTaskId, setTask, clearTask } = usePomodoro();
  const colors = getColors(isDark);
  const interpretTask = useQuickAddInterpreter('task');
  
  // Check if user can add more tasks
  const taskLimit = getItemLimit('maxTasks');
  const canAddTask = canAddMore('maxTasks', tasks.length);
  const isAtLimit = !canAddTask && taskLimit !== Infinity;
  
  // Modal & filter states
  const [showModal, setShowModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [restoreTaskId, setRestoreTaskId] = useState(null);
  const [restoreDueDate, setRestoreDueDate] = useState('');
  
  // Archived tasks filter/sort states
  const [archivedSort, setArchivedSort] = useState('newest'); // 'newest', 'oldest', 'alphabetical'
  const [archivedFilterCategory, setArchivedFilterCategory] = useState('all');
  const [archivedFilterPriority, setArchivedFilterPriority] = useState('all');
  
  // Chart visibility on mobile
  const [showChart, setShowChart] = useState(true);

  // Pomodoro timer visibility (default hidden)
  const [showPomodoro, setShowPomodoro] = useState(false);
  
  // On large screens the chart and timer live in a sidebar, where the timer is always open
  const [isWide, setIsWide] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsWide(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const timerOpen = showPomodoro || isWide;
  
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    category: 'personal',
    weight: 'medium'
  });
  
  // Calculate task urgency score (for visualization)
  const getUrgencyScore = (task) => {
    if (task.completed) return 0;
    
    let score = 0;
    
    // Weight factor
    const weightScores = { critical: 40, high: 30, medium: 20, low: 10 };
    score += weightScores[task.weight] || 20;
    
    // Due date factor
    if (task.dueDate) {
      const daysUntil = Math.ceil((new Date(task.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) score += 50; // Overdue
      else if (daysUntil === 0) score += 40; // Due today
      else if (daysUntil === 1) score += 30; // Due tomorrow
      else if (daysUntil <= 3) score += 20; // Due within 3 days
      else if (daysUntil <= 7) score += 10; // Due within a week
    }
    
    return score;
  };
  
  // Chart data - tasks by category with urgency
  const chartData = useMemo(() => {
    const activeTasks = tasks.filter(t => !t.completed);
    const categoryMap = {};
    
    taskCategories.forEach(cat => {
      categoryMap[cat.value] = { 
        name: cat.label, 
        count: 0, 
        urgency: 0,
        color: cat.color,
        value: cat.value
      };
    });
    
    activeTasks.forEach(task => {
      const cat = task.category || 'other';
      if (categoryMap[cat]) {
        categoryMap[cat].count++;
        categoryMap[cat].urgency += getUrgencyScore(task);
      }
    });
    
    return Object.values(categoryMap).filter(c => c.count > 0);
  }, [tasks]);
  
  // Filter tasks (by category only, since all tasks here are active)
  const filteredTasks = tasks.filter(task => {
    if (filterCategory !== 'all' && task.category !== filterCategory) return false;
    return true;
  });
  
  // Sort by urgency score (highest first)
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    return getUrgencyScore(b) - getUrgencyScore(a);
  });
  
  // Modal handlers
  const openAddModal = (prefill) => {
    setEditingTask(null);
    setTaskForm({
      title: typeof prefill === 'string' ? prefill : '',
      description: '',
      dueDate: '',
      category: 'personal',
      weight: 'medium'
    });
    setShowModal(true);
  };
  
  const openEditModal = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate || '',
      category: task.category,
      weight: task.weight
    });
    setShowModal(true);
  };
  
  // Quick-add: a leading "*" marks the task as priority, as in the bullet-journal key
  const handleQuickAdd = (text) => {
    const priority = text.startsWith('*');
    const title = priority ? text.replace(/^\*+\s*/, '') : text;
    if (!title) return;
    addTask({ title, description: '', dueDate: '', category: 'personal', weight: priority ? 'high' : 'medium' });
  };
  
  const handleSave = () => {
    if (!taskForm.title.trim()) return;
    
    if (editingTask) {
      updateTask(editingTask.id, taskForm);
    } else {
      addTask(taskForm);
    }
    setShowModal(false);
  };
  
  const handleDelete = (id) => {
    deleteTask(id);
  };
  
  // Get category info
  const getCategoryInfo = (categoryId) => {
    return taskCategories.find(c => c.value === categoryId) || taskCategories[0];
  };
  
  // Get weight info
  const getWeightInfo = (weightId) => {
    return weightOptions.find(w => w.value === weightId) || weightOptions[1];
  };
  
  // Helper to parse date string as local date (avoids timezone issues)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    // Parse YYYY-MM-DD as local date, not UTC
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(dateStr);
  };
  
  // Helper to get today as YYYY-MM-DD string
  const getTodayString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };
  
  // Check if task is overdue (compare date strings directly to avoid timezone issues)
  const isOverdue = (task) => {
    if (!task.dueDate || task.completed) return false;
    return task.dueDate < getTodayString();
  };
  
  // Format due date
  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const todayStr = getTodayString();
    const today = new Date();
    const tomorrowStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate() + 1).padStart(2, '0')}`;
    
    if (dateStr === todayStr) return 'Today';
    if (dateStr === tomorrowStr) return 'Tomorrow';
    
    // Parse as local date for display
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Stats
  const activeCount = tasks.length;
  const archivedCount = archivedTasks?.length || 0;
  const overdueCount = tasks.filter(isOverdue).length;

  // Check if user has access to tasks (after all hooks)
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
      </div>
    );
  }
  
  if (!hasFeature('tasks')) {
    return <UpgradePrompt feature="Task Management" description="Organize your work with tasks, due dates, priorities, and categories. Track progress and stay on top of your to-do list." />;
  }

  // Show skeleton while data is loading
  if (dataLoading) {
    return (
      <div className="min-h-screen pb-16 pt-6" style={{ backgroundColor: 'transparent' }}>
        <TasksPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: 'transparent' }}>
      <div>
        
        <PageHead
          title="Tasks"
          meta={`${activeCount} active · ${archivedCount} completed${overdueCount > 0 ? ` · ${overdueCount} overdue` : ''} · sorted by urgency`}
          actions={
            <>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs rounded-md transition-all duration-200 font-medium"
                style={{ 
                  color: colors.textSecondary, 
                  border: `1px solid ${colors.borderStrong}`,
                  backgroundColor: showFilters ? colors.bgTertiary : 'transparent'
                }}
                aria-expanded={showFilters}
                aria-label="Filters"
              >
                <Filter size={14} strokeWidth={1.5} />
                <span className="hidden sm:inline">Filters</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={() => setShowArchivedModal(true)}
                className="flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 text-xs rounded-md transition-all duration-200 font-medium"
                style={{ 
                  color: colors.textSecondary,
                  border: `1px solid ${colors.borderStrong}`
                }}
              >
                <Archive size={14} strokeWidth={1.5} />
                <span className="sm:hidden">Archived</span>
                <span className="hidden sm:inline">Completed</span> ({archivedTasks?.length || 0})
              </button>
              {isAtLimit && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-xs font-medium hover:underline transition-all"
                  style={{ color: colors.accent }}
                >
                  Upgrade →
                </button>
              )}
            </>
          }
        />
        
        {/* Filters */}
        {showFilters && (
          <div 
            className="pt-5 pb-2 mb-6 flex flex-wrap gap-4 animate-fadeIn"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <div>
              <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2.5 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                style={{ 
                  backgroundColor: colors.bg,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`
                }}
              >
                <option value="all">All Categories</option>
                {taskCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        
        {/* lg: list | sticky tools column. Smaller screens: tools above the list */}
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-x-10 lg:items-start">
          <aside className="tasks-tools min-w-0 lg:col-start-2 lg:row-start-1">
          {/* Task Urgency Chart */}
          {chartData.length > 0 && (
            <div 
              className="mb-6 overflow-hidden"
              style={{ borderTop: `1px solid ${colors.textPrimary}`, borderBottom: `1px solid ${colors.border}` }}
            >
              <button
                onClick={() => setShowChart(!showChart)}
                className="w-full py-3 flex items-center justify-between"
              >
                <h3 
                  className="text-[15px] font-semibold tracking-tight"
                  style={{ color: colors.textPrimary }}
                >
                  Demand by category
                </h3>
                <ChevronDown 
                  size={18} 
                  className={`transition-transform duration-150 ${showChart ? 'rotate-180' : ''}`}
                  style={{ color: colors.textMuted }}
                />
              </button>
              {showChart && (
              <div className="pb-4">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={chartData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={80}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: colors.textMuted, fontSize: 12 }}
                  />
                  <Tooltip 
                    cursor={false}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div 
                            className="px-4 py-3 rounded-md "
                            style={{ 
                              backgroundColor: colors.bgSecondary,
                              border: `1px solid ${colors.border}`
                            }}
                          >
                            <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>{data.name}</p>
                            <p className="text-sm" style={{ color: colors.textMuted }}>
                              {data.count} task{data.count !== 1 ? 's' : ''} • Urgency: {data.urgency}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="urgency" barSize={10}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors.textPrimary} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="font-mono text-xs mt-3" style={{ color: colors.textMuted }}>
                Urgency = Priority + Due Date Proximity
              </p>
              </div>
              )}
            </div>
          )}
        
          {/* Focus timer. Phones on the web skip it: the header's timer starts and pauses from any page */}
          <div className={`mb-6 animate-fade-in animate-delay-3 ${Capacitor.isNativePlatform() ? '' : 'hidden md:block'}`}>
            <button
              onClick={() => setShowPomodoro(!showPomodoro)}
              className={`lg:hidden flex items-center gap-2 ${showPomodoro ? 'mb-4' : ''} px-4 py-2.5 text-xs transition-colors duration-150 font-medium`}
              style={{
                color: showPomodoro ? colors.onAccent : colors.textSecondary,
                border: `1px solid ${showPomodoro ? colors.accent : colors.borderStrong}`,
                backgroundColor: showPomodoro ? colors.accent : 'transparent'
              }}
              aria-expanded={showPomodoro}
            >
              <Timer size={14} strokeWidth={1.5} />
              Focus timer
              <ChevronDown
                size={14}
                className="transition-transform duration-150"
                style={{ transform: showPomodoro ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            <div
              className="overflow-hidden transition-all duration-150 ease-in-out"
              style={{
                maxHeight: timerOpen ? '600px' : '0px',
                opacity: timerOpen ? 1 : 0,
              }}
            >
              <PomodoroTimer tasks={tasks} sidebar />
            </div>
          </div>
          </aside>
          
          <div className="min-w-0 lg:col-start-1 lg:row-start-1">
        {/* Soft upgrade nudge - shows when approaching limit */}
        <SoftUpgradeNudge 
          current={tasks.length}
          limit={taskLimit}
          itemType="tasks"
          colors={colors}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        
        {/* Tasks List: the quick-add row is always the first line */}
        <div style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
          <QuickAddRow
            margin=""
            mark={<span className="font-mono text-[17px]">•</span>}
            placeholder="Add a task…"
            hint="* for priority · ↵ to add"
            onAdd={handleQuickAdd}
            interpret={interpretTask}
            onDetails={openAddModal}
            blocked={isAtLimit}
            onBlocked={() => setShowUpgradeModal(true)}
          />
        </div>
        {sortedTasks.length === 0 ? (
          <p className="py-10 text-sm" style={{ color: colors.textMuted }}>
            {tasks.length === 0 ? 'No tasks yet. Type one above and press Enter.' : 'No tasks match your filters'}
          </p>
        ) : (
          <div>
            {sortedTasks.map((task, index) => {
              const category = getCategoryInfo(task.category);
              const weight = getWeightInfo(task.weight);
              const overdue = isOverdue(task);
              const isLocked = taskLimit !== Infinity && index >= taskLimit;
              const isPomodoroActive = activeTaskId === task.id;
              const important = task.weight === 'high' || task.weight === 'critical';
              const due = formatDueDate(task.dueDate);

              return (
                <div key={task.id} data-task-wrapper>
                  <div
                    data-task-card
                    className="nb-row row-actions-host grid grid-cols-[50px_24px_minmax(0,1fr)_auto] sm:grid-cols-[64px_28px_minmax(0,1fr)_auto] gap-x-2.5 sm:gap-x-3.5 items-center min-h-[52px] relative"
                    style={{
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor: isPomodoroActive ? colors.bgTertiary : 'transparent',
                      opacity: isLocked ? 0.4 : 1,
                    }}
                  >
                    {isLocked && (
                      <div
                        className="absolute inset-0 flex items-center justify-end pr-16 cursor-pointer z-10"
                        onClick={() => { window.location.href = '/upgrade'; }}
                        title="Upgrade to Pro to unlock this task"
                      >
                        <Lock size={16} style={{ color: colors.textMuted }} />
                      </div>
                    )}

                    <span
                      className="self-stretch flex items-center font-mono text-xs tabular-nums"
                      style={{ borderRight: `1px solid ${colors.border}`, color: overdue ? colors.textPrimary : colors.textSecondary }}
                    >
                      {due === 'Tomorrow' ? 'Tmrw' : (due || '—')}
                    </span>

                    <button
                      onClick={(e) => {
                        if (isLocked) return;
                        const card = e.currentTarget.closest('[data-task-card]');
                        const wrapper = card?.closest('[data-task-wrapper]');
                        e.currentTarget.textContent = '×';
                        card?.querySelector('.ink-strike')?.classList.add('on');
                        setTimeout(() => {
                          if (wrapper) {
                            wrapper.style.overflow = 'hidden';
                            wrapper.style.maxHeight = wrapper.offsetHeight + 'px';
                            wrapper.offsetHeight; // eslint-disable-line no-unused-expressions
                            wrapper.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
                            wrapper.style.maxHeight = '0';
                            wrapper.style.opacity = '0';
                          }
                          setTimeout(() => archiveTask(task.id), 300);
                        }, 420);
                      }}
                      disabled={isLocked}
                      className={`w-11 h-11 -mx-2.5 flex items-center justify-center font-mono text-[17px] ${isLocked ? 'cursor-not-allowed' : ''}`}
                      style={{ color: colors.textPrimary }}
                      title={isLocked ? 'Upgrade to unlock' : 'Mark as completed'}
                      aria-label={`Complete: ${task.title}`}
                    >
                      •
                    </button>

                    <div className="nb-cell min-w-0 py-3">
                      <div className="flex items-baseline gap-1.5">
                        {important && <span className="font-mono" style={{ color: colors.textPrimary }} title="Priority">*</span>}
                        <span className="ink-strike text-[15px] leading-snug" style={{ color: colors.textPrimary }}>
                          {task.title}
                        </span>
                        {isPomodoroActive && (
                          <span className="font-mono text-[11px] ml-1" style={{ color: colors.textSecondary }}>focusing</span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-xs mt-0.5 line-clamp-1" style={{ color: colors.textMuted }}>{task.description}</p>
                      )}
                    </div>

                    {/* Labels and actions share one slot: on hover (desktop) they trade places; touch shows both */}
                    <div className="row-swap justify-self-end">
                      <div className="row-swap-labels flex items-center gap-3">
                      {overdue && <span className="font-mono text-[11px] underline underline-offset-[3px]" style={{ color: colors.textPrimary }}>overdue</span>}
                      {task.weight === 'critical' && <span className="font-mono text-[11px]" style={{ color: colors.error }}>critical</span>}
                      <span className="hidden sm:inline font-mono text-[11px]" style={{ color: colors.textMuted }} title={`${category.label} · ${weight.label} priority`}>
                        {category.label.toLowerCase()}
                      </span>
                      </div>
                      <div className="row-swap-actions flex items-center">
                        <button
                          onClick={() => {
                            if (!showPomodoro) { setShowPomodoro(true); setTask(task.id, task.title); return; }
                            if (activeTaskId === task.id) clearTask(); else setTask(task.id, task.title);
                          }}
                          className="p-2 transition-opacity hover:opacity-60"
                          style={{ color: isPomodoroActive ? colors.textPrimary : colors.textMuted }}
                          title={activeTaskId === task.id ? 'Remove from focus' : 'Focus on this task'}
                          aria-pressed={isPomodoroActive}
                        >
                          <Timer size={15} strokeWidth={isPomodoroActive ? 2 : 1.5} />
                        </button>
                        <button
                          onClick={() => openEditModal(task)}
                          className="p-2 transition-opacity hover:opacity-60"
                          style={{ color: colors.textMuted }}
                          aria-label={`Edit ${task.title}`}
                        >
                          <Edit2 size={15} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleDelete(task.id)}
                          className="p-2 transition-opacity hover:opacity-60"
                          style={{ color: colors.textMuted }}
                          aria-label={`Delete ${task.title}`}
                        >
                          <Trash2 size={15} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </div>
      </div>
      
      {/* Add/Edit Task Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-lg p-5 sm:p-6 animate-scale-up sm:max-h-[85vh] overflow-y-auto"
            style={{ 
              ...getModalStyle(colors)
            }}
          >
            <div className="flex items-center justify-between mb-5 sm:mb-8">
              <h3 
                className="text-xl font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {editingTask ? 'Edit Task' : 'New Task'}
              </h3>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 -mr-2 rounded-full"
                style={{ color: colors.textMuted }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Title
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="What needs to be done?"
                  className="w-full px-4 py-4 sm:py-3 text-base sm:text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Description (optional)
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Add more details..."
                  rows={3}
                  className="w-full px-4 py-4 sm:py-3 text-base sm:text-sm rounded-md outline-none resize-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <div style={{ minWidth: 0 }}>
                  <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={taskForm.dueDate}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full py-4 sm:py-3 rounded-md outline-none transition-all duration-200 focus:ring-2"
                    style={{ 
                      backgroundColor: colors.bg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                      minWidth: 0,
                      fontSize: '14px',
                      paddingLeft: '12px',
                      paddingRight: '8px',
                      WebkitAppearance: 'none'
                    }}
                  />
                </div>
                
                <div style={{ minWidth: 0 }}>
                  <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                    Category
                  </label>
                  <select
                    value={taskForm.category}
                    onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })}
                    className="w-full py-4 sm:py-3 rounded-md outline-none transition-all duration-200 focus:ring-2"
                    style={{ 
                      backgroundColor: colors.bg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                      minWidth: 0,
                      fontSize: '14px',
                      paddingLeft: '12px',
                      paddingRight: '8px'
                    }}
                  >
                    {taskCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Quick date pills - always visible, highlighted when selected */}
              <div className="flex gap-2">
                {[
                  { label: 'Today', days: 0 },
                  { label: 'Tomorrow', days: 1 },
                  { label: '3 days', days: 3 },
                  { label: '1 week', days: 7 },
                ].map((option) => {
                  const date = new Date();
                  date.setDate(date.getDate() + option.days);
                  const dateStr = date.toISOString().split('T')[0];
                  const isSelected = taskForm.dueDate === dateStr;
                  
                  return (
                    <button
                      key={option.days}
                      onClick={() => {
                        // Toggle: if already selected, clear; otherwise set
                        setTaskForm({ 
                          ...taskForm, 
                          dueDate: isSelected ? '' : dateStr 
                        });
                      }}
                      className="flex-1 py-2 rounded-md text-xs font-medium transition-all duration-200 "
                      style={{
                        backgroundColor: isSelected ? colors.accent : colors.bgTertiary,
                        color: isSelected ? colors.onAccent : colors.textSecondary,
                        border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              
              <div>
                <label className="block text-xs mb-3 font-medium" style={{ color: colors.textMuted }}>
                  Priority
                </label>
                <div className="flex gap-2">
                  {weightOptions.map(option => {
                    const isSelected = taskForm.weight === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTaskForm({ ...taskForm, weight: option.value })}
                        className="flex-1 py-3 px-2 text-center rounded-md transition-all duration-200 "
                        style={{
                          backgroundColor: isSelected ? option.color : `${option.color}15`,
                          color: isSelected ? colors.onAccent : option.color,
                          border: `2px solid ${isSelected ? option.color : `${option.color}30`}`,
                        }}
                      >
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={handleSave}
                className="w-full py-3.5 text-xs rounded-md transition-all duration-200 font-medium"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {editingTask ? 'Save Changes' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Completed/Archived Tasks Modal */}
      {showArchivedModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-h-[85vh] sm:max-w-2xl animate-scaleIn flex flex-col"
            style={getModalStyle(colors)}
          >
            {/* Fixed Header */}
            <div className="p-4 sm:p-6 pb-0 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 
                  className="text-lg sm:text-xl font-semibold tracking-tight"
                  style={{ color: colors.textPrimary }}
                >
                  Completed Tasks
                </h3>
                <button 
                  onClick={() => {
                    setShowArchivedModal(false);
                    setRestoreTaskId(null);
                    setRestoreDueDate('');
                  }}
                  className="p-2 -mr-2 rounded-full"
                  style={{ color: colors.textMuted }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {(!archivedTasks || archivedTasks.length === 0) ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center py-12">
                  <Archive size={40} style={{ color: colors.textMuted }} className="mx-auto mb-4" />
                  <p style={{ color: colors.textMuted }}>
                    No completed tasks yet
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Fixed Sort & Filter Controls */}
                <div className="px-4 sm:px-6 pb-4 flex-shrink-0">
                  <div className="flex flex-wrap gap-2">
                    {/* Sort */}
                    <select
                      value={archivedSort}
                      onChange={(e) => setArchivedSort(e.target.value)}
                      className="px-3 py-2.5 text-xs rounded-md outline-none"
                      style={{ 
                        backgroundColor: colors.bg,
                        color: colors.textPrimary,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="alphabetical">A-Z</option>
                    </select>
                    
                    {/* Category Filter */}
                    <select
                      value={archivedFilterCategory}
                      onChange={(e) => setArchivedFilterCategory(e.target.value)}
                      className="px-3 py-2.5 text-xs rounded-md outline-none"
                      style={{ 
                        backgroundColor: colors.bg,
                        color: colors.textPrimary,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <option value="all">All Categories</option>
                      {taskCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    
                    {/* Priority Filter */}
                    <select
                      value={archivedFilterPriority}
                      onChange={(e) => setArchivedFilterPriority(e.target.value)}
                      className="px-3 py-2.5 text-xs rounded-md outline-none"
                      style={{ 
                        backgroundColor: colors.bg,
                        color: colors.textPrimary,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <option value="all">All Priorities</option>
                      {weightOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Scrollable Task List */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6">
                  <div className="space-y-3">
                {(() => {
                  // Filter archived tasks
                  let filtered = archivedTasks.filter(task => {
                    if (archivedFilterCategory !== 'all' && task.category !== archivedFilterCategory) return false;
                    if (archivedFilterPriority !== 'all' && task.weight !== archivedFilterPriority) return false;
                    return true;
                  });
                  
                  // Sort archived tasks
                  if (archivedSort === 'newest') {
                    filtered = [...filtered].sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));
                  } else if (archivedSort === 'oldest') {
                    filtered = [...filtered].sort((a, b) => new Date(a.completedAt || 0) - new Date(b.completedAt || 0));
                  } else if (archivedSort === 'alphabetical') {
                    filtered = [...filtered].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                  }
                  
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <p style={{ color: colors.textMuted }}>No tasks match your filters</p>
                      </div>
                    );
                  }
                  
                  return filtered.map(task => {
                  const category = getCategoryInfo(task.category);
                  const weight = getWeightInfo(task.weight);
                  const isRestoring = restoreTaskId === task.id;
                  
                  return (
                    <div
                      key={task.id}
                      className="p-4 rounded-lg"
                      style={{ 
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      {/* Title and actions row */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 
                          className="text-sm sm:text-base line-through opacity-70 flex-1 font-semibold tracking-tight"
                          style={{ color: colors.textPrimary }}
                        >
                          {task.title}
                        </h4>
                        
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              if (isRestoring) {
                                setRestoreTaskId(null);
                                setRestoreDueDate('');
                              } else {
                                setRestoreTaskId(task.id);
                                setRestoreDueDate('');
                              }
                            }}
                            className="p-2 rounded-md transition-all duration-200 "
                            style={{ color: colors.accent, backgroundColor: `${colors.accent}15` }}
                            title="Restore task"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => permanentlyDeleteTask(task.id)}
                            className="p-2 rounded-md transition-all duration-200 "
                            style={{ color: colors.error, backgroundColor: `${colors.error}10` }}
                            title="Permanently delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Meta tags */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span 
                          className="text-xs px-2 py-1 rounded-md opacity-70"
                          style={{ backgroundColor: `${category.color}15`, color: category.color }}
                        >
                          {category.label}
                        </span>
                        <span 
                          className="text-xs px-2 py-1 rounded-md opacity-70"
                          style={{ backgroundColor: `${weight.color}15`, color: weight.color }}
                        >
                          {weight.label}
                        </span>
                        {task.completedAt && (
                          <span className="text-xs" style={{ color: colors.textMuted }}>
                            Completed {new Date(task.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      
                      {/* Restore date picker */}
                      {isRestoring && (
                        <div className="mt-4 pt-4 border-t flex items-end gap-3" style={{ borderColor: colors.border }}>
                          <div className="flex-1">
                            <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                              New Due Date
                            </label>
                            <input
                              type="date"
                              value={restoreDueDate}
                              onChange={(e) => setRestoreDueDate(e.target.value)}
                              className="w-full px-4 py-2 text-sm rounded-md outline-none"
                              style={{ 
                                backgroundColor: colors.bgSecondary,
                                color: colors.textPrimary,
                                border: `1px solid ${colors.border}`
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              restoreTask(task.id, restoreDueDate);
                              setRestoreTaskId(null);
                              setRestoreDueDate('');
                            }}
                            className="px-4 py-2 text-xs rounded-md transition-all duration-200 font-medium"
                            style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                          >
                            Restore
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
                })()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {/* Upgrade Limit Modal */}
      <UpgradeLimitModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        itemType="tasks"
        currentCount={tasks.length}
        limit={taskLimit}
      />
    </div>
  );
};

export default TasksPage;
